import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { createLikeButtonMessage } from '../../services/create-like-button-message';
import AnimatedLikeIcon from '../animated-like-icon';
import { Counter } from '../counter';
import { useActions, useSelector } from '../runtime-context';
import styles from './like-button-with-count.scss';

type Props = {
  className?: string;
  postId: string;
  showZero?: boolean;
  switchCounterAndButton?: boolean;
  liked: boolean;
  count: number;
};

const useGetLikeState = (count: number, liked: boolean) => {
  const [state, setState] = useState({ count, liked });

  useEffect(() => {
    setState({ count, liked });
  }, [count, liked]);

  return {
    ...state,
    setState,
  };
};

export const LikeButtonWithCount = ({
  className,
  showZero = false,
  switchCounterAndButton = false,
  postId,
  liked,
  count,
}: Props) => {
  const { t } = useTranslation();
  const actions = useActions();
  const hostId = useSelector((state, host) => host.id);
  const liveRegionHtmlId = `${hostId}-like-button-${postId}`;
  const { count: likeCount, liked: isLiked, setState } = useGetLikeState(count, liked);

  const handleLikeClick = () => {
    actions.incrementPostLikeCount(postId, isLiked);
    setState((prev) => {
      return {
        count: prev.count + (prev.liked ? -1 : 1),
        liked: !prev.liked,
      };
    });
  };

  const ariaLabel = isLiked ? t('like-button.aria-label.unlike') : t('like-button.aria-label.like');
  const postLikesMessage = createLikeButtonMessage({
    t,
    likeCount,
    isLiked,
    isComment: false,
  });

  return (
    <button
      aria-label={ariaLabel}
      aria-describedby={liveRegionHtmlId}
      className={classNames(styles.button, className)}
      onClick={handleLikeClick}
    >
      <span id={liveRegionHtmlId} aria-live="polite" className={styles.srOnly}>
        {postLikesMessage}
      </span>
      <span
        aria-hidden="true"
        className={classNames(
          styles.flexContainer,
          switchCounterAndButton && styles.switchCounterAndButton,
        )}
        data-hook="like-button-with-count__like-count"
      >
        <Counter
          className={classNames(styles.count, 'like-button-with-count__like-count')}
          count={likeCount}
          showZero={showZero}
          ariaHidden={true}
        />
        <AnimatedLikeIcon isLiked={isLiked} />
      </span>
    </button>
  );
};
